import {
  ViewEncapsulation,
  AfterViewInit,
  OnInit,
  OnDestroy,
  QueryList,
  Component,
  Input,
  ElementRef,
  Renderer2,
  ContentChildren,
  ViewContainerRef,
  ViewChild,
} from '@angular/core';
import { Subscription, Subject, Observable, BehaviorSubject } from 'rxjs';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { PgFieldComponent } from '../pg-field.component';
import { PgFieldGridRowComponent } from './pg-field-grid-row/pg-field-grid-row.component';
import { PgSubscriptMessagesComponent } from '../pg-subscript-messages/pg-subscript-messages.component';

import {
  PG_SUBSCRIPT_RENDERER_TOKEN,
  PgSubscriptRenderer,
} from '../pg-subscript-messages/pg-subscript-renderer';

import {
  PgGridLayoutStrategy,
  PgGridSmallViewportLayoutStrategy,
  PgGridLargeViewportLayoutStrategy,
} from './pg-grid-layout-strategy';
import {
  PG_SUBSCRIPT_STATE_CONTROLLER_TOKEN,
  PgSubscriptController,
} from '../pg-subscript-messages/pg-subscript-controller';
import { PgFieldGridSubscriptController } from './pg-field-grid-subscript-controller';

import { debounceTime, delay } from 'rxjs/operators';
import { styleVariables } from '../../styles/variables';

@Component({
  selector: 'pg-field-grid, grit-field-grid',
  templateUrl: `./pg-field-grid.component.html`,
  styleUrls: [`./pg-field-grid.component.scss`],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: PG_SUBSCRIPT_RENDERER_TOKEN,
      useExisting: PgFieldGridComponent,
    },
    {
      provide: PG_SUBSCRIPT_STATE_CONTROLLER_TOKEN,
      useExisting: PgFieldGridComponent,
    },
    PgFieldGridSubscriptController,
  ],
})
export class PgFieldGridComponent
  implements
    AfterViewInit,
    OnInit,
    OnDestroy,
    PgSubscriptRenderer,
    PgSubscriptController {
  @ContentChildren(PgFieldGridRowComponent) fieldRowChildren: QueryList<
    PgFieldGridRowComponent
  >;

  @ContentChildren(PgSubscriptMessagesComponent, { descendants: true })
  _messageChildren: QueryList<PgSubscriptMessagesComponent>;

  @ContentChildren(PgFieldComponent, { descendants: true })
  fieldChildren: QueryList<PgFieldComponent>;

  subs: Subscription[] = [];
  layoutStrategy: PgGridLayoutStrategy;
  private _stateChanges = new Subject();
  public stateChanges: Observable<any>;

  @Input() size: 'sm' | 'md' | null;

  @ViewChild('subscriptMessagesOutlet', { read: ViewContainerRef })
  _subscriptMessagesOutlet: ViewContainerRef;

  @ViewChild('gridWrapper', { static: true })
  _gridWrapperChild: ElementRef;

  get gridWrapperEl(): HTMLElement {
    return this._gridWrapperChild.nativeElement;
  }

  @ViewChild('subscriptWrapper', { static: true })
  _subscriptWrapperChild: ElementRef;
  get subscriptWrapperEl(): HTMLElement {
    return this._subscriptWrapperChild.nativeElement;
  }

  get hostElement(): HTMLElement {
    return this.elementRef.nativeElement;
  }
  // private _subscriptController: PgFieldGridSubscriptController
  constructor(
    public elementRef: ElementRef,
    public renderer: Renderer2,
    private breakpointObserver: BreakpointObserver
  ) {
    this.stateChanges = this._stateChanges.pipe(debounceTime(1));
  }

  ngOnInit() {
    this.subs.push(
      this.breakpointObserver
        .observe([`(min-width: ${styleVariables.widthBreakpointLg}px)`])
        .subscribe((state: BreakpointState) => {
          this.layoutStrategy = state.matches
            ? new PgGridLargeViewportLayoutStrategy(this)
            : new PgGridSmallViewportLayoutStrategy(this);
          this.layoutStrategy.reconcileLayout();
        })
    );
  }
  ngAfterViewInit() {
    this.subs.push(
      this.fieldChildren.changes.pipe(delay(0)).subscribe(children => {
        this._afterFieldChildrenContentUpdate();
      })
    );

    /*
      For some unknown reason this forces the componentChildren changes to trigger
      the first time. If we don't call set timeout, the component children changes
      won't update until the user interacts with the page, causing a bunch
      of styles dependent on that first change to be unsettled.
    */
    setTimeout(() => {}, 0);
  }

  ngOnDestroy() {
    this.subs.forEach(s => !s.closed && s.unsubscribe());
  }

  // Inherited from Subscript interfaces
  renderMessages(host: PgSubscriptMessagesComponent) {
    this._subscriptMessagesOutlet.createEmbeddedView(host.contentTemplate);
  }

  checkSubscriptState(host: PgSubscriptMessagesComponent, animate: boolean) {
    //   this._subscriptController.checkSubscriptState(host, this, animate);
  }

  private _shownSubscriptMessage: BehaviorSubject<{
    element: ElementRef;
    value: string;
  }> = new BehaviorSubject(null);
  public shownSubscriptMessage$: Observable<{
    element: ElementRef;
    value: string;
  }> = this._shownSubscriptMessage.asObservable();

  private _afterFieldChildrenContentUpdate() {
    this.fieldChildren.forEach(r => (r.size = this.size));
    this._initializeFieldListeners();

    this.layoutStrategy.reconcileLayout();
    this.renderer.setStyle(this.hostElement, 'visibility', 'visible');
  }

  private _initializeFieldListeners() {
    this.fieldChildren.forEach(fc => {
      fc.control.stateChanges.subscribe(() => {
        this._stateChanges.next();
        if (fc.control.isFocused) {
          this.renderer.setStyle(fc.elementRef.nativeElement, 'z-index', '5');
        } else if (fc.control.isError) {
          this.renderer.setStyle(fc.elementRef.nativeElement, 'z-index', '4');
        } else {
          this.renderer.setStyle(fc.elementRef.nativeElement, 'z-index', null);
        }
      });
    });
  }
}
